import { Workflow, WorkflowStep } from 'utils/WorkflowUtils'

import { Condition, Edge, FlowExecution, Phase, Task } from '../flow/types'

type GetNextNodeResult = {
  nextNodeId: string | null
  reachedEnd: boolean
}

/**
 * Determines the next node to traverse to from the current node.
 * Handles phases, tasks, conditional branching, and phase exits.
 * Filters out loop edges (edges starting with 'loop-edge-') to prevent infinite loops.
 */
const getNextNode = (
  currentNodeId: string,
  edges: Edge[],
  taskMap: Map<string, Task>,
  phaseMap: Map<string, Phase>,
  conditionMap: Map<string, Condition>
): GetNextNodeResult => {
  // Filter out loop edges to prevent infinite loops
  const nonLoopEdges = edges.filter((edge) => !edge.id.startsWith('loop-edge-'))

  // Handle phase nodes
  if (phaseMap.has(currentNodeId)) {
    // Find the first task inside this phase
    const firstTaskEdge = nonLoopEdges.find((edge) => {
      if (edge.from_id !== currentNodeId) return false
      const targetTask = taskMap.get(edge.to_id)

      return targetTask?.phase_id === currentNodeId
    })

    if (firstTaskEdge) {
      return { nextNodeId: firstTaskEdge.to_id, reachedEnd: false }
    }

    // No tasks in phase, try to find continuation edge
    const continuationEdge = nonLoopEdges.find(
      (edge) => edge.from_id === currentNodeId
    )

    if (continuationEdge) {
      return { nextNodeId: continuationEdge.to_id, reachedEnd: false }
    }

    // No edges found, end of flow
    return { nextNodeId: null, reachedEnd: true }
  }

  // Handle task nodes
  const task = taskMap.get(currentNodeId)

  if (!task) {
    return { nextNodeId: null, reachedEnd: true }
  }

  // Find outgoing edges from this task (excluding loop edges)
  const outgoingEdges = nonLoopEdges.filter((edge) => edge.from_id === task.id)

  // If no outgoing edges from task, check if we need to exit the phase
  if (
    outgoingEdges.length === 0 ||
    (outgoingEdges.length === 1 && !outgoingEdges[0].to_id)
  ) {
    if (task.phase_id) {
      // Find the edge that goes out of the phase
      const phaseEdges = nonLoopEdges.filter(
        (edge) => edge.from_id === task.phase_id
      )

      const phaseOutgoingEdge = phaseEdges.find((edge) => {
        const targetPhase = phaseMap.get(edge.to_id)

        if (targetPhase) return true
        const targetTask = taskMap.get(edge.to_id)

        return targetTask?.phase_id !== task.phase_id
      })

      if (phaseOutgoingEdge) {
        return { nextNodeId: phaseOutgoingEdge.to_id, reachedEnd: false }
      }
    }

    // Reached the end
    return { nextNodeId: null, reachedEnd: true }
  }

  // If only one outgoing edge, follow it
  if (outgoingEdges.length === 1) {
    return { nextNodeId: outgoingEdges[0].to_id, reachedEnd: false }
  }

  // Multiple outgoing edges - this is a conditional branching point
  // Find the winner branch (edge with condition that has is_met = true)
  const winnerEdge = outgoingEdges.find((edge) => {
    if (!edge.condition_id) {
      return false
    }

    const condition = conditionMap.get(edge.condition_id)

    return condition?.is_met === true
  })

  // If we found a winner branch, follow it
  if (winnerEdge) {
    if (winnerEdge.to_id) {
      return { nextNodeId: winnerEdge.to_id, reachedEnd: false }
    } else if (!winnerEdge.to_id && task.phase_id) {
      // decision task inside the phase where the selected edge leads to the end of the phase
      // Find the edge that goes out of the phase
      const phaseEdges = nonLoopEdges.filter(
        (edge) => edge.from_id === task.phase_id
      )

      const phaseOutgoingEdge = phaseEdges.find((edge) => {
        const targetPhase = phaseMap.get(edge.to_id)

        if (targetPhase) return true
        const targetTask = taskMap.get(edge.to_id)

        return targetTask?.phase_id !== task.phase_id
      })

      if (phaseOutgoingEdge) {
        return { nextNodeId: phaseOutgoingEdge.to_id, reachedEnd: false }
      }
    }
  }

  // If no winner branch found (conditional task not evaluated), stop here
  // This means the decision hasn't been made yet
  return { nextNodeId: null, reachedEnd: false }
}

/**
 * Determines the active execution path through the flow graph
 * by traversing edges starting from 'trigger' node, following the winner branch
 * when encountering conditional tasks with multiple outgoing edges
 */
export const getActiveFlowPath = (
  flow: FlowExecution
): { tasks: Task[]; isPathComplete: boolean } => {
  if (!flow?.tasks || !flow?.edges) {
    return { tasks: [], isPathComplete: false }
  }

  const { tasks, edges, phases } = flow

  // Create task lookup map
  const taskMap = new Map(tasks.map((task) => [task.id, task]))

  // Create phase lookup map
  const phaseMap = new Map(phases?.map((phase) => [phase.id, phase]) || [])

  // Create condition lookup map from all tasks
  const conditionMap = new Map<string, Condition>()

  tasks.forEach((task) => {
    task.conditions?.forEach((condition) => {
      conditionMap.set(condition.id, condition)
    })
  })

  // Track visited to avoid cycles
  const visited = new Set<string>()
  const activePath: Task[] = []
  let isPathComplete = true

  // Traverse from a given node ID (can be task or phase)
  const traverse = (startNodeId: string) => {
    let currentNodeId: string | null = startNodeId

    while (currentNodeId) {
      // Check if already visited to avoid cycles
      if (visited.has(currentNodeId)) {
        break
      }

      visited.add(currentNodeId)

      // Add task to active path if it's ECP-enabled and it's a task node
      const task = taskMap.get(currentNodeId)

      if (task?.ecp?.enabled) {
        activePath.push(task)
      }

      // Get the next node using the extracted function
      const { nextNodeId, reachedEnd } = getNextNode(
        currentNodeId,
        edges,
        taskMap,
        phaseMap,
        conditionMap
      )

      if (!reachedEnd && nextNodeId === null) {
        // This means we stopped at an unevaluated decision task
        isPathComplete = false
      }

      // Move to the next node
      currentNodeId = nextNodeId
    }
  }

  // Start traversal from the 'trigger' node
  // There is always exactly one edge from trigger, so pick that one
  const triggerEdge = edges.find((edge) => edge.from_id === 'trigger')

  if (triggerEdge?.to_id) {
    traverse(triggerEdge.to_id)
  }

  return { tasks: activePath, isPathComplete }
}

/**
 * Transform a task into a step
 */
const transformTaskToStep = (task: Task, index: number): WorkflowStep => {
  let stepStatus: 'COMPLETED' | 'SKIPPED' | 'IN_PROGRESS' = 'IN_PROGRESS'

  if (task.status === 'COMPLETED' || task.status === 'SKIPPED') {
    stepStatus = 'COMPLETED'
  } else if (
    task.status === 'PENDING' ||
    task.status === 'CONDITION_PENDING' ||
    task.status === 'IN_PROGRESS'
  ) {
    stepStatus = 'IN_PROGRESS'
  }

  const step: WorkflowStep = {
    assignedTo: task.assigned_to,
    ecp: {
      description: task.ecp?.description,
      enabled: task.ecp?.enabled || false,
      label: task.ecp?.label || task.name
    },
    installer: {
      description: task.installer?.description,
      enabled: task.installer?.enabled || false,
      label: task.installer?.label || task.name
    },
    id: task.id,
    journey: task.journey
      ? {
          id: task.journey.id,
          journeyId: task.journey.journeyId,
          name: task.journey.name
        }
      : undefined,
    label: task.name,
    lastUpdated: (task.analytics as any)?.completed_at || task.updated_at,
    name: task.name,
    order: index,
    status: stepStatus,
    type: 'STEP' as const
  }

  return step
}

const hasEcpEnabledTask = (flow: FlowExecution): boolean => {
  return flow.tasks?.some((task) => task.ecp?.enabled) ?? false
}

const getEcpVisibility = (flow: FlowExecution): boolean => {
  return flow.available_in_ecp ?? hasEcpEnabledTask(flow)
}

/**
 * Transform a flow execution into a workflow
 */
const transformToWorkflow = (flow: FlowExecution): Workflow => {
  let workflowStatus = 'STARTED'

  if (flow.status === 'COMPLETED') {
    workflowStatus = 'DONE'
  } else if (flow.status === 'CLOSED' || flow.status === 'PAUSED') {
    workflowStatus = 'CLOSED'
  } else if (flow.status === 'STARTED') {
    workflowStatus = 'STARTED'
  }

  const workflow: Workflow = {
    assignedTo: flow.assigned_to || [],
    configuredClosingReasonSnapshot:
      flow.closing_reason?.configured_reasons || [],
    contexts:
      flow.contexts?.map((ctx) => ({
        id: ctx.entity_id,
        schema: ctx.entity_schema,
        title: ''
      })) || [],
    creationTime: flow.created_at,
    definitionId: flow.flow_template_id,
    enableECPWorkflow: getEcpVisibility(flow),
    flow: [],
    id: flow.wfExecId || flow.id,
    lastUpdateTime: flow.updated_at,
    name: flow.name,
    nextOpenStep: {
      entityRefId: '',
      id: ''
    },
    orgId: flow.org_id || flow.orgId,
    selectedClosingReasons: null,
    status: workflowStatus,
    trigger: flow.trigger?.type || 'MANUAL',
    updateEntityAttributes: [],
    version: flow.version || 3
  }

  return workflow
}

/**
 * Transform a flow execution's active path into a steps array
 */
const transformToSteps = (flow: FlowExecution) => {
  const { tasks: activeTasks, isPathComplete } = getActiveFlowPath(flow)

  if (activeTasks.length === 0) {
    return { steps: [], isPathComplete: true }
  }

  const steps = activeTasks.map((task, index) =>
    transformTaskToStep(task, index)
  )

  return { steps, isPathComplete }
}

/**
 * Check if a flow execution is version 3
 */
export const isWorkflowV3 = (
  data: Workflow | FlowExecution
): data is FlowExecution => {
  return data?.version === 3 && !!data?.tasks
}

/**
 * Convert a flow execution into a linear workflow
 */
export const convertToLinearWorkflow = (flow: FlowExecution): Workflow => {
  const workflow = transformToWorkflow(flow)
  const { steps, isPathComplete } = transformToSteps(flow)

  return {
    ...workflow,
    isConvertedToLinearWf: true,
    isActivePathComplete: isPathComplete,
    flow: [...steps] as unknown as Workflow['flow']
  }
}
